/**
 * Translation-layer module for praxis execution-runtime literals
 * (praxis-hardening D9). Praxis owns the TYPE truth — `ExecutionStatus`,
 * `ParkKind` are imported type-only; runtime tuples are declared locally with
 * bidirectional type-completeness assertions. Same Convex-bundle constraint
 * as `praxis-beads.ts`: praxis's `dist/core.mjs` transitively pulls a dead
 * `node:module` import the Convex V8 isolate can't resolve, so runtime values
 * are never imported from praxis here — the assertions pin the local tuples to
 * praxis's authoritative types at compile time instead.
 *
 * Display labels + the parked-snapshot guard live here so every clawcraft
 * consumer (the execution hook, ExecutionTab banners, RunTagStrip) single-
 * sources them. Pure module — no Convex, no React, no external libs. Pinned by
 * `test/unit/domain/praxis-executions.test.ts`.
 */

import type {
  ExecutionStatus as PraxisExecutionStatus,
  ParkKind as PraxisParkKind,
} from "@soulbound-labs/praxis";

// ─── Runtime tuples (single-sourced) ─────────────────────────────────
export const EXECUTION_STATUSES = ["in_flight", "completed", "abandoned"] as const;
export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number];

export const PARK_KINDS = ["human", "timer", "event"] as const;
export type ParkKind = (typeof PARK_KINDS)[number];

// ─── Bidirectional type-completeness assertions ──────────────────────
// If praxis adds/removes a status or park kind and these tuples don't
// follow suit, tsc fails compile (same pattern as praxis-beads.ts).
type _AllPraxisStatusesCovered = PraxisExecutionStatus extends ExecutionStatus
  ? true
  : never;
type _NoExtraLocalStatuses = ExecutionStatus extends PraxisExecutionStatus
  ? true
  : never;
const _statusA: _AllPraxisStatusesCovered = true;
void _statusA;
const _statusB: _NoExtraLocalStatuses = true;
void _statusB;

type _AllPraxisParkKindsCovered = PraxisParkKind extends ParkKind ? true : never;
type _NoExtraLocalParkKinds = ParkKind extends PraxisParkKind ? true : never;
const _parkA: _AllPraxisParkKindsCovered = true;
void _parkA;
const _parkB: _NoExtraLocalParkKinds = true;
void _parkB;

// ─── Display labels ──────────────────────────────────────────────────
export const EXECUTION_STATUS_DISPLAY_LABEL: Record<ExecutionStatus, string> = {
  in_flight: "Running",
  completed: "Completed",
  abandoned: "Abandoned",
};

export const PARK_KIND_DISPLAY_LABEL: Record<ParkKind, string> = {
  human: "Waiting on a person",
  timer: "Scheduled",
  event: "Waiting on an event",
};

// ─── Parked snapshot shape + guard ───────────────────────────────────
// Mirrors praxis `ParkedSnapshot` / the Convex `executions.parked` column.
// The frontend reads live Convex rows; the guard narrows an untyped value
// (e.g. a row whose optional `parked` may be null/undefined) before render.
export interface ParkedSnapshotBead {
  readonly factoryId: string;
  readonly shortId: string;
  readonly type: string;
  readonly title: string;
  readonly kind: string;
  readonly dueAt: number | null;
  readonly eventName: string | null;
}
export interface ParkedSnapshot {
  readonly kind: ParkKind;
  readonly dueAt: number | null;
  readonly parkedAt: number;
  readonly beads: readonly ParkedSnapshotBead[];
}

const PARK_KIND_SET: ReadonlySet<string> = new Set(PARK_KINDS);

export function isParkedSnapshot(value: unknown): value is ParkedSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const s = value as Record<string, unknown>;
  if (typeof s.kind !== "string" || !PARK_KIND_SET.has(s.kind)) return false;
  if (s.dueAt !== null && typeof s.dueAt !== "number") return false;
  if (typeof s.parkedAt !== "number") return false;
  if (!Array.isArray(s.beads)) return false;
  return true;
}

// ─── Display state (execution card / banner axis) ────────────────────
// The one UI-facing state a run is rendered in. Precedence pinned from
// praxis-doctrine §10.9's healthy-park / failure-route / true-wedge table:
// terminal outranks a park; a park (healthy — has an assigned bead)
// outranks a needs-attention failure route; otherwise the run is running.
// Note: no "failed" or "held" member — an errored/held run surfaces via
// its `status`/`needsUserBeads` inputs, never as its own display literal.
export const EXECUTION_DISPLAY_STATES = [
  "running",
  "parked",
  "needs_attention",
  "completed",
  "abandoned",
] as const;
export type ExecutionDisplayState = (typeof EXECUTION_DISPLAY_STATES)[number];

export function deriveExecutionDisplayState({
  status,
  parked,
  needsUserBeads,
}: {
  status: ExecutionStatus;
  parked: ParkedSnapshot | null;
  needsUserBeads: boolean;
}): ExecutionDisplayState {
  // 1. Terminal outranks everything.
  if (status === "completed") return "completed";
  if (status === "abandoned") return "abandoned";
  // 2. A healthy park (assigned bead) outranks the failure route.
  if (parked !== null) return "parked";
  // 3. Failure route: a user-assigned bead is blocking while the run continues.
  if (needsUserBeads) return "needs_attention";
  // 4. Otherwise the run is live.
  return "running";
}
